import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    LogOut,
    AlertTriangle,
    Loader2,
    Menu,
    X,
    Eye,
    LayoutDashboard,
    User as UserIcon,
    HeartHandshake,
    Globe2,
    Newspaper,
    FolderOpen,
    BarChart3,
    Settings,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import AssoSidebar from "../components/AssoSidebar";
import AssoDashboard from "../components/AssoDashboard";
import AssoAnalytics from "../components/AssoAnalytics";
import AssoSettings from "../components/AssoSettings";
import AssoCompletionWidget from "../components/AssoCompletionWidget";
import AssociationFiche from "../components/AssociationFiche";
import Logo from "../components/Logo";
import EditableHero from "../components/partner/EditableHero";
import GratitudeMessagesTab from "../components/partner/admin/GratitudeMessagesTab";
import ImpactStoriesTab from "../components/partner/admin/ImpactStoriesTab";
import AssoNewsTab from "../components/partner/admin/AssoNewsTab";
import AssoResourcesTab from "../components/partner/admin/AssoResourcesTab";

/**
 * Iter-27.73 — Backoffice asso premium en mode "creator dashboard".
 *
 * Architecture :
 *   - Sidebar gauche (cf. AssoSidebar) avec carte profil + nav
 *   - Top bar mobile (menu hamburger) car la sidebar est cachée < lg
 *   - Main content : section selon currentTab (dashboard / space / gratitude /
 *     impact / news / analytics / settings)
 *   - Modal de confirmation avant logout
 */
const TAB_TITLES = {
    dashboard: { title: "Tableau de bord", subtitle: "Vue d'ensemble de ton activité" },
    space: { title: "Mon espace", subtitle: "Personnalise ce que les ambassadeurs voient" },
    gratitude: { title: "Remerciements", subtitle: "Envoie des messages personnels et émotionnels" },
    impact: { title: "Histoires d'impact", subtitle: "Raconte les vies que ton action transforme" },
    news: { title: "Actualités", subtitle: "Tiens tes ambassadeurs informés en temps réel" },
    resources: { title: "Ressources", subtitle: "Partage des documents utiles à tes ambassadeurs" },
    ambassador_view: { title: "Vue ambassadeur", subtitle: "Ta fiche exactement comme la voient tes ambassadeurs" },
    analytics: { title: "Statistiques", subtitle: "Mesure l'engagement et l'impact de ton espace" },
    settings: { title: "Paramètres", subtitle: "Compte, sécurité et préférences" },
};

const MOBILE_NAV = [
    { id: "dashboard", Icon: LayoutDashboard, label: "Accueil" },
    { id: "space", Icon: UserIcon, label: "Espace" },
    { id: "ambassador_view", Icon: Eye, label: "Vue ambassadeur" },
    { id: "gratitude", Icon: HeartHandshake, label: "Mercis" },
    { id: "impact", Icon: Globe2, label: "Impact" },
    { id: "news", Icon: Newspaper, label: "Actus" },
    { id: "resources", Icon: FolderOpen, label: "Ressources" },
    { id: "analytics", Icon: BarChart3, label: "Stats" },
    { id: "settings", Icon: Settings, label: "Réglages" },
];

export default function AssoAdmin() {
    const { logout } = useAuth();
    const [asso, setAsso] = useState(null);
    const [stats, setStats] = useState(null);
    const [tab, setTab] = useState("dashboard");
    const [logoutOpen, setLogoutOpen] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer
    const [loggingOut, setLoggingOut] = useState(false);

    const reloadAsso = async () => {
        try {
            const r = await api.get("/asso/me");
            setAsso(r.data);
        } catch {
            setAsso(false);
        }
    };

    const reloadStats = async () => {
        try {
            const r = await api.get("/asso/me/stats");
            setStats(r.data);
        } catch {
            /* noop */
        }
    };

    useEffect(() => {
        reloadAsso();
        reloadStats();
    }, []);

    // Reload stats à chaque changement d'onglet de contenu (counts à jour)
    useEffect(() => {
        if (asso && ["gratitude", "impact", "news", "resources"].includes(tab)) {
            reloadStats();
        }
    }, [tab, asso]);

    const confirmLogout = () => {
        setLoggingOut(true);
        logout(); // Iter-33 — hard redirect géré par logout()
    };

    if (asso === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-shariti-deep-black">
                <Loader2 className="w-6 h-6 animate-spin text-shariti-orange" />
            </div>
        );
    }
    if (asso === false) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-shariti-deep-black p-8 text-center">
                <div className="rounded-3xl p-8 max-w-md bg-white/3 border border-white/10">
                    <p className="text-white/85 font-bold mb-2">Aucune association liée</p>
                    <p className="text-sm text-white/55 mb-5">
                        Ton compte n'est pas (ou plus) rattaché à une association. Contacte
                        l'équipe Shariti Academy.
                    </p>
                    <button
                        onClick={confirmLogout}
                        className="bg-white/5 hover:bg-white/10 text-white/85 font-bold px-4 py-2 rounded-2xl text-sm"
                    >
                        Se déconnecter
                    </button>
                </div>
            </div>
        );
    }

    const tabInfo = TAB_TITLES[tab] || TAB_TITLES.dashboard;
    const counts = stats?.counts || {};
    const sidebarCounts = {
        gratitude: counts.gratitude,
        impact: counts.impact,
        news: counts.news,
        resources: counts.resources,
    };

    return (
        <div
            className="min-h-screen bg-shariti-deep-black flex"
            data-testid="asso-admin-root"
        >
            {/* Sidebar desktop */}
            <AssoSidebar
                asso={asso}
                stats={stats}
                currentTab={tab}
                onTab={(id) => {
                    setTab(id);
                    setSidebarOpen(false);
                }}
                onLogoutRequest={() => setLogoutOpen(true)}
                counts={sidebarCounts}
            />

            {/* Sidebar mobile drawer */}
            <AnimatePresence>
                {sidebarOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="lg:hidden fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
                        onClick={() => setSidebarOpen(false)}
                    >
                        <motion.div
                            initial={{ x: -300 }}
                            animate={{ x: 0 }}
                            exit={{ x: -300 }}
                            transition={{ type: "spring", damping: 24 }}
                            onClick={(e) => e.stopPropagation()}
                            className="h-screen"
                        >
                            <div className="lg:!hidden">
                                <SidebarMobile
                                    asso={asso}
                                    stats={stats}
                                    currentTab={tab}
                                    onTab={(id) => {
                                        setTab(id);
                                        setSidebarOpen(false);
                                    }}
                                    onLogoutRequest={() => {
                                        setSidebarOpen(false);
                                        setLogoutOpen(true);
                                    }}
                                    counts={sidebarCounts}
                                    onClose={() => setSidebarOpen(false)}
                                />
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main */}
            <main className="flex-1 min-w-0 flex flex-col">
                {/* Top bar — Iter-30.27 : refonte mobile (titre ne tronque plus, sous-titre wrap 2 lignes max,
                    bouton "Aperçu" devient icône seule sur très petits écrans) */}
                <header className="sticky top-0 z-30 backdrop-blur-xl bg-shariti-deep-black/85 border-b border-white/5">
                    <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4 lg:py-0 lg:h-[72px] flex items-center gap-2 sm:gap-3">
                        <button
                            onClick={() => setSidebarOpen(true)}
                            data-testid="asso-mobile-menu"
                            aria-label="Ouvrir le menu"
                            className="lg:hidden shrink-0 p-2 -ml-1 rounded-xl hover:bg-white/5 text-white/70"
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                        <div className="flex-1 min-w-0">
                            <p className="text-[9px] sm:text-[10px] font-thin tracking-[0.28em] sm:tracking-[0.3em] uppercase text-shariti-yellow truncate leading-tight">
                                {tabInfo.title}
                            </p>
                            <h1 className="text-[13px] sm:text-base font-extrabold text-white leading-tight line-clamp-2 sm:line-clamp-1 mt-0.5">
                                {tabInfo.subtitle}
                            </h1>
                        </div>
                        {asso?.association_id && (
                            <a
                                href={`/espace-partenaires/${asso.association_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="asso-preview-ambassador-btn"
                                title="Voir l'espace comme un ambassadeur (nouvel onglet)"
                                aria-label="Aperçu ambassadeur"
                                className="shrink-0 inline-flex items-center gap-1.5 bg-shariti-yellow/12 hover:bg-shariti-yellow/20 border border-shariti-yellow/30 text-shariti-yellow font-extrabold px-2.5 sm:px-3 py-2 rounded-xl text-xs sm:text-[13px] transition-all"
                            >
                                <Eye className="w-3.5 h-3.5 shrink-0" />
                                <span className="hidden sm:inline">Aperçu ambassadeur</span>
                                <span className="sm:hidden">Aperçu</span>
                            </a>
                        )}
                    </div>
                </header>

                {/* Content */}
                <div className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24">
                    {tab === "dashboard" && (
                        <AssoDashboard stats={stats} onJumpToTab={setTab} />
                    )}

                    {tab === "space" && (
                        <div className="space-y-4">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-shariti-yellow/30 bg-shariti-yellow/10 mb-2">
                                <span className="text-[11px] font-extrabold tracking-wider text-shariti-yellow">
                                    APERÇU LIVE — clique sur les crayons pour modifier
                                </span>
                            </div>
                            <EditableHero
                                data={asso}
                                onUpdated={(d) => setAsso(d)}
                                gratitudeCount={counts.gratitude || 0}
                                impactCount={counts.impact || 0}
                            />
                        </div>
                    )}

                    {tab === "gratitude" && (
                        <GratitudeMessagesTab
                            assoId={asso.association_id}
                            useAssoEndpoints
                            onChanged={reloadStats}
                        />
                    )}

                    {tab === "impact" && (
                        <ImpactStoriesTab
                            assoId={asso.association_id}
                            useAssoEndpoints
                            onChanged={reloadStats}
                        />
                    )}

                    {tab === "news" && <AssoNewsTab onChanged={reloadStats} />}

                    {tab === "resources" && <AssoResourcesTab onChanged={reloadStats} />}

                    {tab === "ambassador_view" && (
                        <div data-testid="ambassador-view-tab">
                            <AssociationFiche
                                associationId={asso.association_id}
                                previewMode
                            />
                        </div>
                    )}

                    {tab === "analytics" && <AssoAnalytics stats={stats} />}

                    {tab === "settings" && <AssoSettings asso={asso} />}
                </div>
            </main>

            {/* Modal logout */}
            <AnimatePresence>
                {logoutOpen && (
                    <LogoutConfirmModal
                        asso={asso}
                        loggingOut={loggingOut}
                        onCancel={() => setLogoutOpen(false)}
                        onConfirm={confirmLogout}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}


function SidebarMobile({ asso, stats, currentTab, onTab, onLogoutRequest, counts, onClose }) {
    const accent = asso?.primary_color || "#EE722E";
    return (
        <div className="w-72 h-screen flex flex-col border-r border-white/5 bg-shariti-deep-black overflow-y-auto">
            <div className="flex items-center justify-between px-4 pt-5 pb-2">
                <div className="flex items-center gap-2.5">
                    <Logo
                        variant="stamp"
                        className="w-8 h-8 shrink-0 ring-1 ring-white/10"
                    />
                    <div className="leading-tight">
                        <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-white/55">
                            Shariti
                        </p>
                        <p
                            className="text-[10px] font-thin tracking-[0.45em] uppercase"
                            style={{
                                color: "transparent",
                                WebkitTextStroke: "0.6px #F89F63",
                            }}
                        >
                            Academy
                        </p>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="p-1.5 rounded-lg hover:bg-white/5 text-white/65"
                    aria-label="Fermer"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            <div className="p-4 m-3 rounded-3xl bg-white/3 border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                    {asso?.logo_url && (
                        <div className="w-10 h-10 rounded-xl bg-white p-1 flex items-center justify-center shrink-0">
                            <img
                                src={asso.logo_url}
                                alt=""
                                className="w-full h-full object-contain"
                            />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <p className="text-[9px] font-thin tracking-[0.25em] uppercase text-white/55">
                            Backoffice
                        </p>
                        <h2 className="text-sm font-extrabold text-white truncate">
                            {asso?.name || "—"}
                        </h2>
                    </div>
                </div>
                <AssoCompletionWidget
                    asso={asso}
                    stats={stats}
                    accent={accent}
                    onGoToTab={onTab}
                />
            </div>

            <nav className="px-3 space-y-1 flex-1 pb-3">
                {MOBILE_NAV.map((item) => {
                    const Icon = item.Icon;
                    const isActive = currentTab === item.id;
                    const count = counts[item.id];
                    return (
                        <button
                            key={item.id}
                            onClick={() => onTab(item.id)}
                            data-testid={`asso-mobile-nav-${item.id}`}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-bold transition-all ${
                                isActive
                                    ? "bg-shariti-orange/15 text-white"
                                    : "text-white/55 hover:text-white hover:bg-white/5"
                            }`}
                        >
                            <Icon className="w-4 h-4" />
                            <span className="flex-1 text-left">{item.label}</span>
                            {count !== undefined && count > 0 && (
                                <span className="text-[10px] font-extrabold bg-white/10 text-white/65 px-1.5 py-0.5 rounded-full">
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </nav>

            <div className="px-3 pb-4 pt-2 border-t border-white/5">
                <button
                    onClick={onLogoutRequest}
                    className="w-full inline-flex items-center gap-2 px-3 py-2.5 rounded-2xl text-sm font-bold text-white/55 hover:text-red-300 hover:bg-red-500/10 transition-all"
                >
                    <LogOut className="w-4 h-4" />
                    Se déconnecter
                </button>
            </div>
        </div>
    );
}


function LogoutConfirmModal({ asso, loggingOut, onCancel, onConfirm }) {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onCancel}
            data-testid="logout-confirm-modal"
        >
            <motion.div
                initial={{ scale: 0.95, y: 10, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-shariti-black border border-white/10 rounded-3xl w-full max-w-md p-6 sm:p-7 shadow-2xl"
            >
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-11 h-11 rounded-2xl bg-red-500/15 border border-red-500/30 flex items-center justify-center shrink-0">
                        <AlertTriangle className="w-5 h-5 text-red-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-red-300 mb-0.5">
                            Confirmation
                        </p>
                        <h3 className="text-lg font-extrabold text-white leading-tight">
                            Quitter ton backoffice ?
                        </h3>
                    </div>
                </div>

                <p className="text-sm text-white/65 font-light leading-relaxed mb-5">
                    Tu es sur le point de te déconnecter de l'espace{" "}
                    <span className="text-shariti-yellow font-bold">{asso?.name}</span>. Tu devras
                    te reconnecter avec ton mot de passe pour revenir.
                </p>

                {/* Récap mini de l'espace */}
                <div className="rounded-2xl p-3 mb-5 bg-white/3 border border-white/10">
                    <div className="flex items-center gap-2">
                        {asso?.logo_url && (
                            <div className="w-8 h-8 rounded-lg bg-white p-1 shrink-0">
                                <img
                                    src={asso.logo_url}
                                    alt=""
                                    className="w-full h-full object-contain"
                                />
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-extrabold text-white truncate">
                                {asso?.name}
                            </p>
                            {asso?.tier && (
                                <p className="text-[10px] text-white/55 font-thin tracking-[0.18em] uppercase truncate">
                                    {tierShortLabel(asso.tier)}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex gap-2 justify-end">
                    <button
                        onClick={onCancel}
                        disabled={loggingOut}
                        data-testid="logout-cancel"
                        className="bg-white/5 hover:bg-white/10 text-white/80 font-bold px-4 py-2.5 rounded-2xl text-sm disabled:opacity-40"
                    >
                        Rester connecté
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loggingOut}
                        data-testid="logout-confirm"
                        className="inline-flex items-center gap-2 bg-red-500/90 hover:bg-red-500 text-white font-extrabold px-5 py-2.5 rounded-2xl text-sm disabled:opacity-40"
                    >
                        {loggingOut ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <LogOut className="w-4 h-4" />
                        )}
                        Se déconnecter
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
}


function tierShortLabel(tier) {
    return (
        {
            signature: "Partenaire Signature",
            premium: "Partenaire Premium",
            impact: "Partenaire Impact",
            engaged: "Partenaire Engagé",
        }[tier] || "Partenaire"
    );
}
